import React, { useState } from 'react'
import {
  TextField,
  Box,
  Button,
  ButtonGroup,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Typography,
} from '@mui/material'
import { Info as InfoIcon } from '@mui/icons-material'

interface CronHelperProps {
  value: string
  onChange: (value: string) => void
}

const CronHelper: React.FC<CronHelperProps> = ({ value, onChange }) => {
  const [helperMode, setHelperMode] = useState<'simple' | 'advanced'>('simple')
  const [tipo, setTipo] = useState<'diario' | 'semanal' | 'mensal' | 'personalizado'>('diario')
  const [hora, setHora] = useState<number>(2)
  const [minuto, setMinuto] = useState<number>(0)
  const [diaSemana, setDiaSemana] = useState<number>(0)
  const [diaMes, setDiaMes] = useState<number>(1)

  const generateCron = () => {
    if (tipo === 'diario') {
      return `${minuto} ${hora} * * *`
    } else if (tipo === 'semanal') {
      return `${minuto} ${hora} * * ${diaSemana}`
    } else if (tipo === 'mensal') {
      return `${minuto} ${hora} ${diaMes} * *`
    }
    return value
  }

  const handleSimpleChange = () => {
    const newCron = generateCron()
    onChange(newCron)
  }

  React.useEffect(() => {
    if (helperMode === 'simple') {
      handleSimpleChange()
    }
  }, [tipo, hora, minuto, diaSemana, diaMes, helperMode])

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2">Agendamento (Cron)</Typography>
        <ButtonGroup size="small">
          <Button
            variant={helperMode === 'simple' ? 'contained' : 'outlined'}
            onClick={() => setHelperMode('simple')}
          >
            Assistente
          </Button>
          <Button
            variant={helperMode === 'advanced' ? 'contained' : 'outlined'}
            onClick={() => setHelperMode('advanced')}
          >
            Avançado
          </Button>
        </ButtonGroup>
      </Box>

      {helperMode === 'simple' ? (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={tipo}
                onChange={(e) => setTipo(e.target.value as any)}
                label="Tipo"
              >
                <MenuItem value="diario">Diário</MenuItem>
                <MenuItem value="semanal">Semanal</MenuItem>
                <MenuItem value="mensal">Mensal</MenuItem>
                <MenuItem value="personalizado">Personalizado</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {tipo !== 'personalizado' && (
            <>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth>
                  <InputLabel>Hora</InputLabel>
                  <Select
                    value={hora}
                    onChange={(e) => setHora(Number(e.target.value))}
                    label="Hora"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <MenuItem key={i} value={i}>
                        {i.toString().padStart(2, '0')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6} sm={2}>
                <FormControl fullWidth>
                  <InputLabel>Minuto</InputLabel>
                  <Select
                    value={minuto}
                    onChange={(e) => setMinuto(Number(e.target.value))}
                    label="Minuto"
                  >
                    {[0, 15, 30, 45].map((m) => (
                      <MenuItem key={m} value={m}>
                        {m.toString().padStart(2, '0')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {tipo === 'semanal' && (
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Dia da Semana</InputLabel>
                    <Select
                      value={diaSemana}
                      onChange={(e) => setDiaSemana(Number(e.target.value))}
                      label="Dia da Semana"
                    >
                      <MenuItem value={0}>Domingo</MenuItem>
                      <MenuItem value={1}>Segunda-feira</MenuItem>
                      <MenuItem value={2}>Terça-feira</MenuItem>
                      <MenuItem value={3}>Quarta-feira</MenuItem>
                      <MenuItem value={4}>Quinta-feira</MenuItem>
                      <MenuItem value={5}>Sexta-feira</MenuItem>
                      <MenuItem value={6}>Sábado</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {tipo === 'mensal' && (
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Dia do Mês</InputLabel>
                    <Select
                      value={diaMes}
                      onChange={(e) => setDiaMes(Number(e.target.value))}
                      label="Dia do Mês"
                    >
                      {Array.from({ length: 31 }, (_, i) => (
                        <MenuItem key={i + 1} value={i + 1}>
                          {i + 1}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </>
          )}

          {tipo === 'personalizado' && (
            <Grid item xs={12}>
              <TextField
                value={value}
                onChange={(e) => onChange(e.target.value)}
                label="Expressão Cron"
                fullWidth
                helperText="Formato: minuto hora dia mês dia_semana (ex: 0 2 * * *)"
              />
            </Grid>
          )}
        </Grid>
      ) : (
        <TextField
          value={value}
          onChange={(e) => onChange(e.target.value)}
          label="Expressão Cron"
          fullWidth
          helperText="Formato: minuto hora dia mês dia_semana (ex: 0 2 * * * = diário às 02:00)"
          InputProps={{
            endAdornment: <InfoIcon sx={{ color: 'action.active', mr: 1 }} />,
          }}
        />
      )}
    </Box>
  )
}

export default CronHelper

